import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import type { SessionInfo } from "@techplanner/shared/core";
import { useAuth } from "../auth";
import { Alert, Button, Field } from "../components/ui";
import { api, errorMessage } from "../lib/api";

export function LoginPage() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const [username, setUsername] = useState(() => localStorage.getItem("tp:last-username") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (user) return <Navigate to={from} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      const res = await api<SessionInfo>("/auth/login", { method: "POST", body: { username, password } });
      localStorage.setItem("tp:last-username", username.trim().toLowerCase());
      signIn(res.user);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setPassword("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login">
      <img className="login-logo" src="/icons/icon.svg" alt="" width={72} height={72} />
      <h1>TechPlanner</h1>
      <p className="text-2" style={{ marginTop: 6, marginBottom: 28 }}>
        Contrats de maintenance et interventions.
      </p>
      <form className="form" onSubmit={submit} noValidate>
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Identifiant" htmlFor="username">
          <input
            id="username"
            className="input"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </Field>
        <Field label="Mot de passe" htmlFor="password">
          <div style={{ position: "relative" }}>
            <input
              id="password"
              className="input"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              enterKeyHint="go"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ paddingRight: 96 }}
            />
            <button
              type="button"
              className="btn ghost small"
              style={{ position: "absolute", right: 4, top: 6 }}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? "Masquer" : "Afficher"}
            </button>
          </div>
        </Field>
        <Button type="submit" variant="primary" size="large" block loading={loading} disabled={!username || !password}>
          Se connecter
        </Button>
        <p className="small muted" style={{ textAlign: "center" }}>
          Mot de passe oublié ? Demandez à un administrateur de le réinitialiser.
        </p>
      </form>
    </main>
  );
}
